import { css, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { StyleInfo, styleMap } from 'lit/directives/style-map';
import { booleanConverter } from '../common/converters';
import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { TeAccordionItem } from './te-accordion-item';

/**
 * Accordion Component
 *
 * @slot - Default slot for accordion items
 */
@customElement('te-accordion')
export class TeAccordion extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-accordion {
        margin: 0;
        display: flex;
        flex-direction: column;
        background: ${theme.colors.shade0};
        width: fit-content;
        border-radius: 4px;
        border: 1px solid ${theme.colors.shade2};
        overflow: hidden;
      }

      ::slotted(*:last-child) {
        border-bottom: none;
      }
    `,
  ];
  /** Allow expanding multiple items simultaneously */
  @property({ type: Boolean, converter: booleanConverter })
  expandMultiple = false;
  /** Minimum width for accordion */
  @property({ type: Number }) minWidth = 448;
  /** Maximum width for accordion */
  @property({ type: Number }) maxWidth?: number;

  // This state determines if the open/closed logic is internal or external (user)
  @state() isInternallyControlled = true;
  @state() options: TeAccordionItem[] = [];

  isTeAccordionItem(elem: Element): elem is TeAccordionItem {
    return elem.nodeName === 'TE-ACCORDION-ITEM';
  }

  firstUpdated() {
    this.options = [...this.children].filter(this.isTeAccordionItem);
  }

  getStyle(): StyleInfo {
    if (this.maxWidth === undefined) return { minWidth: this.minWidth + 'px' };
    return {
      minWidth: Math.min(this.minWidth, this.maxWidth) + 'px',
      maxWidth: this.maxWidth + 'px',
    };
  }

  checkInternalOpenState(child: TeAccordionItem) {
    if (this.expandMultiple) return;
    this.options.forEach((option) => {
      if (option !== child) {
        option.internalOpenState = false;
      }
    });
  }

  render() {
    return html`
      <div class="te-accordion" style=${styleMap(this.getStyle())}>
        <slot></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-accordion': TeAccordion;
  }
}
